'use client';

import { useState } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { NumericFormat } from 'react-number-format';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { Dropdown } from '@/components/Dropdown';
import { MobileFilterFooter } from '@/components/Filters/MobileFilterFooter';
import { MobileFilterHeader } from '@/components/Filters/MobileFilterHeader';
import { SearchFilterTrigger } from '@/components/Filters/SearchFilterTrigger';
import baseFilterStyles from '@/components/Filters/baseFilterStyles.module.css';
import { getCurrencySymbolFromCountry } from '@/modules/currencies/helpers';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';

type YMALPriceFilterProps = {
  minPrice?: number;
  maxPrice?: number;
  priceRange: {
    min: number;
    max: number;
  };
  onPriceFilterChange: (min?: number, max?: number) => void;
  onClearFilters: () => void;
  closeMobileFilterDrawer: () => void;
  isFilterableYMALExperiment?: boolean;
};

export function YMALPriceFilter({
  minPrice,
  maxPrice,
  priceRange,
  onPriceFilterChange,
  onClearFilters,
  closeMobileFilterDrawer,
  isFilterableYMALExperiment,
}: YMALPriceFilterProps) {
  const [newPriceValues, setNewPriceValues] = useState<{
    priceMin?: number;
    priceMax?: number;
  }>({
    priceMin: minPrice,
    priceMax: maxPrice,
  });
  const [hasError, setHasError] = useState<boolean>(false);
  const searchT = useTranslations('search');
  const t = useTranslations('common');
  const { location } = useCurrentLocation();
  const currencySymbol = getCurrencySymbolFromCountry(location);
  const title = t('Price');

  function handleMinPriceChange({ value }: { value: string }) {
    setHasError(false);
    setNewPriceValues({ ...newPriceValues, priceMin: Number(value) });
  }

  function handleMaxPriceChange({ value }: { value: string }) {
    setHasError(false);
    setNewPriceValues({ ...newPriceValues, priceMax: Number(value) });
  }

  function handleReset(toggleDropdown: () => void) {
    setHasError(false);
    setNewPriceValues({ priceMin: undefined, priceMax: undefined });
    onPriceFilterChange(undefined, undefined);
    toggleDropdown();
  }

  function handleSubmit(toggleDropdown: () => void) {
    if (
      newPriceValues.priceMin &&
      newPriceValues.priceMax &&
      newPriceValues.priceMin > newPriceValues.priceMax
    ) {
      return setHasError(true);
    }

    onPriceFilterChange(newPriceValues.priceMin, newPriceValues.priceMax);
    toggleDropdown();
  }

  function handleMobileSubmit() {
    if (
      newPriceValues.priceMin &&
      newPriceValues.priceMax &&
      newPriceValues.priceMin > newPriceValues.priceMax
    ) {
      return setHasError(true);
    }

    onPriceFilterChange(newPriceValues.priceMin, newPriceValues.priceMax);
    closeMobileFilterDrawer();
  }

  if (priceRange.min >= priceRange.max) {
    return null;
  }

  return (
    <Dropdown
      TriggerComponent={(props) => (
        <SearchFilterTrigger
          {...props}
          isActive={Boolean(minPrice) || Boolean(maxPrice)}
          title={title}
        />
      )}
    >
      {({ className, toggleDropdown }) => (
        <div
          className={clsx(
            className,
            baseFilterStyles.searchFilterDropdownContents,
            styles.priceFilterDropdownContents
          )}
        >
          <MobileFilterHeader
            title={title}
            toggleDropdown={toggleDropdown}
            closeMobileFilterDrawer={closeMobileFilterDrawer}
          />
          <div className={baseFilterStyles.content}>
            <div className={styles.priceFilterInputRow}>
              <div className={styles.priceFilterInputContainer}>
                <label
                  className={styles.priceFilterInputLabel}
                  htmlFor="ymalPriceMin"
                >
                  {searchT('Min')}
                </label>
                <NumericFormat
                  className={clsx(styles.priceFilterInput, {
                    [styles.priceFilterInputError]: hasError,
                  })}
                  aria-label={searchT('MinimumPrice')}
                  prefix={currencySymbol}
                  placeholder={`${currencySymbol}1`}
                  value={newPriceValues.priceMin || null}
                  onValueChange={handleMinPriceChange}
                  allowNegative={false}
                  decimalScale={0}
                  name="ymalPriceMin"
                  id="ymalPriceMin"
                />
              </div>
              <hr className={styles.priceFilterHr} />
              <div className={styles.priceFilterInputContainer}>
                <label
                  className={styles.priceFilterInputLabel}
                  htmlFor="ymalPriceMax"
                >
                  {searchT('Max')}
                </label>
                <NumericFormat
                  className={styles.priceFilterInput}
                  aria-label={searchT('MaximumPrice')}
                  prefix={currencySymbol}
                  placeholder="any"
                  value={newPriceValues.priceMax || null}
                  onValueChange={handleMaxPriceChange}
                  allowNegative={false}
                  decimalScale={0}
                  name="ymalPriceMax"
                  id="ymalPriceMax"
                />
              </div>
            </div>
            {hasError && (
              <Caption
                variant="lg-regular"
                className={styles.priceFilterErrorMessage}
              >
                {searchT('PriceFilter.ErrorMessage')}
              </Caption>
            )}
            <div className={styles.priceFilterButtonRow}>
              <div className={styles.priceFilterButtonWrapper}>
                <ButtonExperiment
                  variant="secondary"
                  size="small"
                  legacySize="lg"
                  fullWidth
                  legacyClassName={styles.priceFilterButton}
                  legacyChildren={
                    <Caption as="span" variant="lg-regular">
                      {searchT('Reset')}
                    </Caption>
                  }
                  onClick={() => {
                    handleReset(toggleDropdown);
                  }}
                >
                  {searchT('Reset')}
                </ButtonExperiment>
              </div>
              <div className={styles.priceFilterButtonWrapper}>
                <ButtonExperiment
                  size="small"
                  legacySize="lg"
                  fullWidth
                  legacyClassName={styles.priceFilterButton}
                  legacyChildren={
                    <Caption as="span" variant="lg-regular">
                      {searchT('Done')}
                    </Caption>
                  }
                  onClick={() => {
                    handleSubmit(toggleDropdown);
                  }}
                >
                  {searchT('Done')}
                </ButtonExperiment>
              </div>
            </div>
          </div>
          <MobileFilterFooter
            handleClear={onClearFilters}
            handleSubmit={handleMobileSubmit}
            hideResultsCount={true}
            isFilterableYMALExperiment={isFilterableYMALExperiment}
          />
        </div>
      )}
    </Dropdown>
  );
}
